import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@r/utils/utils.ts';
import { Slot } from '@radix-ui/react-slot';

import { Label } from './label.tsx';
import { Help } from './help.tsx';

const baseStyles = {
  base: [
    'peer',
    'flex',
    'w-full',
    'border',
    'font-light',
    'border-input',
    'bg-background',
    'ring-offset-background',
    'placeholder:text-muted-foreground',
    'rounded-md'
  ],
  focus: ['focus-visible:outline-none'],
  disabled: ['disabled:cursor-not-allowed', 'disabled:opacity-30']
};

const variantsStyles = {
  default: ['focus-visible:border-primary'],
  error: ['border-red-400', 'text-red-400', 'placeholder:text-red-400']
};

const textareaVariants = cva(
  [...baseStyles.base, ...baseStyles.focus, ...baseStyles.disabled].join(' '),
  {
    variants: {
      variant: {
        default: [...variantsStyles.default].join(' '),
        error: [...variantsStyles.error].join(' ')
      },
      textareaSize: {
        default: 'text-sm min-h-[5rem] rounded-md px-3 py-2',
        sm: 'text-xs min-h-[3.75rem] rounded px-2 py-1',
        lg: 'text-lg min-h-[6.25rem] rounded-lg px-4 py-3'
      }
    },
    defaultVariants: {
      variant: 'default',
      textareaSize: 'default'
    }
  }
);

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  name?: string;
  disabled?: boolean;
  placeholder?: string;
  backgroundColor?: string;
  color?: string;
  displayLabel?: boolean;
  label?: string;
  displayHelp?: boolean;
  help?: string;
  textareaSize?: 'default' | 'sm' | 'lg';
  asChild?: boolean;
  error?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      name = 'textarea',
      disabled,
      className,
      textareaSize,
      displayLabel,
      label,
      displayHelp,
      help,
      placeholder,
      error,
      variant,
      backgroundColor,
      color,
      asChild = false,
      ...props
    },
    ref
  ) => {
    variant = error ? 'error' : variant;

    const Comp = asChild ? Slot : 'textarea';

    const labelElement = (
      <Label labelSize={textareaSize} state={disabled ? 'disabled' : 'default'} content={label} />
    );
    const labelComp = displayLabel ? labelElement : '';

    const helpElement = <Help helpSize={textareaSize} error={error} help={help} />;
    const helpComp = displayHelp ? helpElement : '';

    return (
      <div className="form-item-group">
        {labelComp}

        <Comp
          name={name}
          disabled={disabled}
          className={cn(textareaVariants({ variant, textareaSize, className }))}
          style={{ backgroundColor, color }}
          placeholder={placeholder}
          ref={ref}
          {...props}
        />

        {helpComp}
      </div>
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea };
